import {
  FilterType,
  TCurrentCheckboxListValue,
  TCurrentFilter,
  TCurrentFilterTextBoxValue,
  TCurrentFilterValue,
  TCurrentTextBox,
  TFilter,
  TFilterTag,
  TGroupedFilter
} from '../../commonTypes/filters';
import { DATEPICKER_YEARS_NUMBER, FilterComponentTypes } from './const';
import { TBaseFilterState, TFilterStateProperties } from './redux/types';
import { LOCAL_STORAGE_KEYS } from './redux/constants';

export const getCurrentFilterValueByName = (data: TCurrentFilter[], name: string): TCurrentFilterValue => {
  if (!data?.length) {
    return null;
  }

  const filter = data.find((el) => el.internalName === name);
  if (filter) {
    return filter.values;
  }

  return null;
};

export const getQuickDatePickerPlaceholder = (data: TCurrentFilter[], name: string): string => {
  const values = getCurrentFilterValueByName(data, name);

  if (!values) {
    return null;
  }
  if (values && values[0] === values[1]) {
    return values[0] as string;
  }
  return `${values[0]} - ${values[1]}`;
};

export const getCurrentTextBoxByName = (data: TCurrentTextBox[], name: string): TCurrentTextBox => {
  if (!data.length) {
    return null;
  }

  const textBox = data.find((el) => el.internalName === name);
  if (textBox) {
    return textBox;
  }

  return null;
};

export const formatDate = (date: Date, month: string[]): string => {
  const d = date.getDate();
  const m = month[date.getMonth()];
  const y = date.getFullYear();
  return m + ' ' + (d <= 9 ? '0' + d : d) + ', ' + y;
};

export const getDefaultDatepickerYears = (): number[] => {
  const curYear = new Date().getFullYear();
  const defaultYears = [];
  const diff = Math.floor(DATEPICKER_YEARS_NUMBER / 2);
  for (let i = curYear - diff; i <= curYear + diff; i++) {
    defaultYears.push(i);
  }
  return defaultYears;
};

export const getCheckboxTitleByValue = (date: TFilter[], name: string, value: number | number[]): string => {
  const defaultValues = date.find((filter) => filter.internalName === name).defaultValues;
  return defaultValues.find((el) => el.value === value || isArraysEqual(el.value, value)).title;
};

export const getCheckboxValueByTitle = (date: TFilter[], name: string, title: string): number => {
  const defaultValues = date.find((filter) => filter.internalName === name).defaultValues;
  return defaultValues.find((el) => el.title === title).value;
};

export const isArraysEqual = (first: unknown, second: unknown): boolean => {
  if (!Array.isArray(first) || !Array.isArray(second) || first.length !== second.length) {
    return false;
  }

  return first.every((el, i) => el === second[i]);
}

export const includes = (array: unknown[], value: unknown): boolean => {
  if (!Array.isArray(array)) {
    return false;
  }

  return array.some(el => el === value || isArraysEqual(el, value));
}

export const getTagsByFilterType = (
  currentFilter: TCurrentFilter,
  filters: TFilter[],
  type: FilterType
): TFilterTag | TFilterTag[] => {
  const isCheckBoxes =
    type === FilterType.TextBox || type === FilterType.CheckBoxList || type === FilterType.CheckBoxTreeList;

  switch (true) {
    case isCheckBoxes: {
      const textBoxValues: TFilterTag[] = [];
      const filterValues = currentFilter.values as TCurrentFilterTextBoxValue | TCurrentCheckboxListValue;
      filterValues.forEach((value: string | number) => {
        const tagItem = {
          name:
            type === FilterType.TextBox
              ? (value as string)
              : getCheckboxTitleByValue(filters, currentFilter.internalName, value as number),
          internalName: currentFilter.internalName,
          type
        };
        textBoxValues.push(tagItem);
      });
      return [...textBoxValues];
    }

    case type === FilterType.MoneyRange:
      return {
        name: `$${currentFilter.values[0]} - $${currentFilter.values[1]}`,
        internalName: currentFilter.internalName,
        type
      };

    default:
      return {
        name: `${currentFilter.values[0]} - ${currentFilter.values[1]}`,
        internalName: currentFilter.internalName,
        type
      };
  }
};

export const groupFilters = (filters: TFilter[]): TGroupedFilter[] => {
  const grouped: Record<string, TGroupedFilter> = filters.reduce((acc, item: TFilter, index) => {
    const key = item.groupDisplayName ? item.groupDisplayName : String(index);

    if (!acc[key]) {
      acc[key] = {
        group: item.groupDisplayName ? item.groupDisplayName : null,
        items: [],
        order: index
      };
    }

    acc[key].items.push(item);

    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => (a.order > b.order ? 1 : -1));
};

export const saveFilterState = (filters: Pick<TBaseFilterState, TFilterStateProperties>, type: FilterComponentTypes): void => {
  const key = LOCAL_STORAGE_KEYS[type];
  if (!key) {
    return null;
  }

  const { currentSortItem, currentFilterItems, currentTextBoxItems } = filters;
  localStorage.setItem(key, JSON.stringify({
    currentSortItem,
    currentFilterItems,
    currentTextBoxItems
  }));
};

export const getFilterState = (type: FilterComponentTypes): Pick<TBaseFilterState, TFilterStateProperties> => {
  const key = LOCAL_STORAGE_KEYS[type];
  if (!key) {
    return null;
  }

  return JSON.parse(localStorage.getItem(key));
};
